// BCM · Goods Receipt Dashboard — non-blocking "Updating Batch" toast.
//
// Rendered by GRFeedbackContext while a status-change poll runs, and kept on
// screen afterwards in the timeout / error cases so the user is told the
// workflow didn't settle within the poll window and offered a manual retry —
// same information density the original "Workflow Triggered" dialog design
// carried, but as a passive bottom-right toast instead of a modal.
//
// Replaces the app-wide blocking backdrop for GR dashboard actions.
//
// outcome:
//   'polling' — poll is still running. Hourglass + progress bar + elapsed s.
//                No buttons; the toast is entirely passive and will close on its
//                own when the poll settles.
//   'timeout' — the 45s cap ran out without a status flip. Warning icon,
//                "Refresh now" button that fires the caller's refetch, Dismiss.
//   'error'   — 3+ transient errors talking to the status endpoint. Error icon,
//                "Refresh now" + Dismiss.
//   'done'    — status flipped; parent auto-closes shortly after (kept for
//                completeness; the parent doesn't currently render this state).
//
// Props:
//   open       : boolean  — controls the Snackbar
//   outcome    : one of the four above (default 'polling')
//   elapsedMs  : number   — ms since the poll started (drives bar + counter
//                           when outcome === 'polling'; ignored otherwise)
//   actionLabel: string   — e.g. "Mark Review as Complete" (optional)
//   onRefreshNow: () => void  — the refetch to run when the user clicks Refresh now
//   onDismiss  : () => void   — close the toast without refetching
import React from "react";
import { Snackbar, Paper, Typography, Box, Button } from "@material-ui/core";
import HourglassEmptyIcon from "@material-ui/icons/HourglassEmpty";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import HighlightOffIcon from "@material-ui/icons/HighlightOff";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import { GR_FONT_FAMILY } from "../goodsReceiptStyles";

// Fills the bar visually at ~20s; the real poll cap is 45s in
// GRFeedbackContext.pollUntilStatusChanges.
const EXPECTED_MS = 20000;

const OUTCOME = {
  polling: {
    Icon: HourglassEmptyIcon,
    color: "#e36e39",
    defaultTitle: "Updating batch",
    titleSuffix: " triggered",
    body:
      "Workflow triggered on the server. Batches usually update in 10–20 seconds. " +
      "We'll refresh automatically as soon as the update lands.",
  },
  timeout: {
    Icon: ErrorOutlineIcon,
    color: "#b04a1e",
    defaultTitle: "Batch still updating",
    titleSuffix: " triggered",
    body:
      "The workflow is still processing on the server. You can refresh manually " +
      "in a moment, or dismiss this and continue working.",
  },
  error: {
    Icon: HighlightOffIcon,
    color: "#8f2222",
    defaultTitle: "Update not confirmed",
    titleSuffix: " triggered",
    body:
      "We couldn't confirm the update automatically. Please refresh manually " +
      "in a moment.",
  },
  done: {
    Icon: CheckCircleIcon,
    color: "#2f7a4a",
    defaultTitle: "Batch updated",
    titleSuffix: " complete",
    body: "Batch updated. Refreshing…",
  },
};

const UpdatingBatchToast = ({
  open,
  outcome = "polling",
  elapsedMs = 0,
  actionLabel,
  onRefreshNow,
  onDismiss,
}) => {
  const spec = OUTCOME[outcome] || OUTCOME.polling;
  const { Icon } = spec;
  const secs = Math.max(0, Math.round(elapsedMs / 1000));
  const pct = Math.min(100, (elapsedMs / EXPECTED_MS) * 100);
  const title = actionLabel ? `${actionLabel}${spec.titleSuffix}` : spec.defaultTitle;
  const showBar = outcome === "polling";
  const showButtons = outcome === "timeout" || outcome === "error";
  return (
    <Snackbar
      open={open}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      style={{ zIndex: 2000 }}
    >
      <Paper
        elevation={6}
        style={{
          padding: "16px 18px 14px",
          borderRadius: 8,
          minWidth: 340,
          maxWidth: 400,
          fontFamily: GR_FONT_FAMILY,
          backgroundColor: "#FFFFFF",
          border: "1px solid #EAE6DA",
        }}
      >
        <Box display="flex" alignItems="center" style={{ gap: 10, marginBottom: 6 }}>
          <Icon style={{ color: spec.color, fontSize: 22 }} />
          <Typography style={{ fontWeight: 600, fontSize: "0.95rem", color: "#1a1c22" }}>
            {title}
          </Typography>
        </Box>
        <Typography
          variant="body2"
          style={{ color: "#4a4e58", fontSize: "0.83rem", lineHeight: 1.5, marginBottom: showBar ? 10 : 14 }}
        >
          {spec.body}
        </Typography>
        {showBar && (
          <>
            <div
              style={{
                height: 4,
                width: "100%",
                background: "#F0ECE3",
                borderRadius: 2,
                overflow: "hidden",
                marginBottom: 4,
              }}
            >
              <div
                style={{
                  height: "100%",
                  width: `${pct}%`,
                  background: spec.color,
                  borderRadius: 2,
                  transition: "width 300ms linear",
                }}
              />
            </div>
            <Typography
              variant="caption"
              style={{ fontFamily: "IBM Plex Mono, monospace", fontSize: 11, color: "#8a8c92" }}
            >
              {secs}s elapsed
            </Typography>
          </>
        )}
        {showButtons && (
          <Box display="flex" justifyContent="flex-end" style={{ gap: 8 }}>
            <Button
              onClick={onDismiss}
              size="small"
              style={{ textTransform: "none", color: "#4a4e58", fontWeight: 500 }}
            >
              Dismiss
            </Button>
            <Button
              onClick={onRefreshNow}
              size="small"
              variant="contained"
              disableElevation
              style={{
                textTransform: "none",
                backgroundColor: "#e36e39",
                color: "#FFFFFF",
                fontWeight: 500,
              }}
            >
              Refresh now
            </Button>
          </Box>
        )}
      </Paper>
    </Snackbar>
  );
};

export default UpdatingBatchToast;
